import React, {useCallback, useMemo} from "react";
import {useRef, useEffect, useState} from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  Node,
  Edge,
  ReactFlowProvider,
  ProOptions,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  useReactFlow,
  useStore,
  ConnectionMode,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {cn} from "@/lib/utils";
import Settings from "./Settings";
import {motion, AnimatePresence} from "framer-motion";
import {
  AssistantType,
  FlowMode,
  SavingState,
  FlowNodeData,
  FlowNodeTypes,
} from "@/types/node";

import useLayout from "@/hooks/useLayout";
import {createNodeTypes} from "../NodeTypes";
import {createEdgeTypes} from "../EdgeTypes";

import "@xyflow/react/dist/style.css";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {useSubflowToReactFlow} from "@/hooks/useSubflowToReactFlow";
import Topbar from "./Topbar";
import {Loader2} from "lucide-react";
import {SaveProvider, useSave} from "@/contexts/SaveContext";
import {Minimap} from "./Minimap";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {PageLeaveGuard} from "./Modals/PageLeaveGuard";
import Toolbar from "./Toolbar";
import {DrawerProvider, useDrawer} from "@/contexts/DrawerContext";
import {
  RunTests,
  Sections,
  Search as SearchDrawer,
  Variables,
  History,
  InitializeAction,
  DRAWER_IDS,
} from "@/components/Drawers";
import {CopyPasteProvider} from "@/contexts/CopyPasteContext";
import {VariablesProvider} from "@/contexts/VariablesContext";
import {SubflowsProvider, useSubflows} from "@/contexts/SubflowsContext";
import {TooltipProvider} from "@/components/ui/tooltip";
import {useUndoRedo} from "@/hooks/useUndoRedo";
import {serializeFlowData} from "@/utils/index";
import * as amplitude from "@amplitude/analytics-browser";
import * as analytics from "@amplitude/analytics-browser";
import {ReactQueryProvider} from "@/providers/ReactQueryProvider";
import {ActionListContextProvider} from "@/contexts/ActionListContext";
import {ActionDetailsContextProvider} from "@/contexts/ActionDetailsContext";
import SubflowManager from "@/components/Subflows/SubflowManager";
declare global {
  interface Window {
    engagement: {
      plugin: () => any;
      boot: (opts: {
        user?: {
          user_id?: string;
          user_email?: string;
          device_id?: string;
          user_properties?: Record<string, unknown>;
        };
        serverZone?: string;
        integrations: {
          track: (event: any) => void;
        }[];
      }) => Promise<void>;
      gs: {
        show: (key: string) => void;
        closeAll: () => void;
      };
    };
  }
}

const proOptions: ProOptions = {account: "paid-pro", hideAttribution: true};

interface CanvasProps {
  className: string;
}

const zoomSelector = s => s.transform[2] <= 0.4;

const Canvas: React.FC<CanvasProps> = ({className}) => {
  const {takeSnapshot, undo, redo, canUndo, canRedo} = useUndoRedo();
  const {
    initialFlowData,
    flowMode,
    savingState,
    setHasUnsavedChanges,
    permanentBlockSave,
  } = useSave();
  const {closeDrawer} = useDrawer();
  const {mainFlow, subflows} = useSubflows();
  const [assistantType] = useState<AssistantType>(null);

  // Sync selected subflow with ReactFlow
  useSubflowToReactFlow();

  const isDemoMode = FlowBuilderSettings.demoMode;
  const [showPrompt, setShowPrompt] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [flowDisabled] = useState(false);

  const reactFlowWrapper = useRef(null);

  const showContext = useStore(zoomSelector);
  const {getViewport} = useReactFlow();

  const prevFlowModeRef = useRef<FlowMode | null>(null);

  useEffect(() => {
    prevFlowModeRef.current = flowMode;
  }, [flowMode]);

  const nodeTypes = useMemo(() => createNodeTypes(showContext), [showContext]);
  const edgeTypes = useMemo(
    () => createEdgeTypes(takeSnapshot),
    [takeSnapshot],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const {getSelectedNode} = useSelectedElements();
  const [selectedNode, setSelectedNode] = useState<Node<FlowNodeData> | null>(
    null,
  );

  const onMove = useCallback(() => {
    const zoom = getViewport().zoom;
    setZoomLevel(Math.round(zoom * 100));
  }, [getViewport]);

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      setEdges(
        deleted.reduce((acc, node) => {
          const connectedEdges = getConnectedEdges([node], edges);
          const filteredEdges = edges.filter(e => e.type !== "floating");

          const incomers = getIncomers(
            node as Node<FlowNodeData>,
            nodes,
            filteredEdges,
          );

          const outgoers = getOutgoers(
            node as Node<FlowNodeData>,
            nodes,
            filteredEdges,
          );

          // Remove edges connected to the deleted node
          const remainingEdges = acc.filter(
            edge => !connectedEdges.includes(edge),
          );

          // Check if the parent (incomer) has more than one child (outgoing connections) to ignore Placeholder nodes
          const createdEdges = incomers.flatMap(({id: source}) => {
            const siblings = edges.filter(edge => edge.source === source);
            if (siblings.length > 1) {
              return [];
            }

            return outgoers.map(({id: target}) => ({
              id: `${source}->${target}`,
              source,
              target,
              type: "workflow",
            }));
          });

          return [...remainingEdges, ...createdEdges];
        }, edges),
      );

      takeSnapshot();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, edges, takeSnapshot],
  );

  useEffect(() => {
    const selected = getSelectedNode() as Node<FlowNodeData> | null;

    if (!selected) {
      closeDrawer(DRAWER_IDS.INITIALIZE_ACTION);
    }
    setSelectedNode(prevSelected => {
      if (prevSelected !== selected) {
        return selected;
      }
      return prevSelected;
    });
  }, [nodes, edges]);

  // Memoize the current serialized flow data to avoid redundant calculations
  const modeKey = flowMode === FlowMode.LIVE ? "live" : "test";

  // Memoize main flow serialization
  const currentMainFlowData = useMemo(
    () =>
      serializeFlowData(
        mainFlow.flowData[modeKey].nodes,
        mainFlow.flowData[modeKey].edges,
      ),
    [mainFlow.flowData, modeKey],
  );

  // Memoize subflows serialization
  const currentSubflowsData = useMemo(
    () =>
      subflows.map(subflow =>
        serializeFlowData(
          subflow.flowData[modeKey].nodes,
          subflow.flowData[modeKey].edges,
        ),
      ),
    [subflows, modeKey],
  );

  // Memoize final combined serialized data
  const currentAllFlowsData = useMemo(
    () =>
      JSON.stringify({
        mainFlow: currentMainFlowData,
        subflows: currentSubflowsData,
      }),
    [currentMainFlowData, currentSubflowsData],
  );

  useEffect(() => {
    const selected = getSelectedNode() as Node<FlowNodeData> | null;
    if (selected !== selectedNode) {
      setSelectedNode(selected);
    }

    const modeKey = flowMode === FlowMode.LIVE ? "live" : "test";
    const hasUnsavedChanges =
      (mainFlow.flowData[modeKey].nodes.length > 0 ||
        subflows.some(sf => sf.flowData[modeKey].nodes.length > 0)) &&
      currentAllFlowsData !== initialFlowData;

    setHasUnsavedChanges(hasUnsavedChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAllFlowsData, initialFlowData, mainFlow, subflows, flowMode]);

  useLayout();

  return (
    <>
      {flowDisabled && (
        <div className="absolute top-0 left-0 w-full h-full bg-white/50 z-50"></div>
      )}
      {!isDemoMode && savingState !== SavingState.ERROR_LOADING && (
        <>
          {FlowBuilderSettings.minimapEnabled && (
            <Minimap zoomLevel={zoomLevel} />
          )}
          <Toolbar
            undoRedoDetails={{
              undo,
              redo,
              canUndo,
              canRedo,
            }}
          />
        </>
      )}
      {!permanentBlockSave && <SubflowManager />}
      <section className={cn("", className)} ref={reactFlowWrapper}>
        {savingState === SavingState.LOADING ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="size-10 animate-spin" />
          </div>
        ) : savingState === SavingState.ERROR_LOADING ? (
          // Hide canvas if there's a load error
          // This prevents interaction with empty/corrupted canvas after load failure
          <div className="flex justify-center items-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-sm">
                There was an error loading your flow. Please refresh the page.
              </p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            proOptions={proOptions}
            fitView
            deleteKeyCode={[]}
            nodesDraggable={false}
            zoomOnDoubleClick={true}
            maxZoom={1.5}
            minZoom={0.25}
            nodesConnectable={false}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodesDelete={onNodesDelete}
            connectionMode={ConnectionMode.Loose}
            onMove={onMove}
          >
            <Background bgColor="#fafafa" gap={8} size={1.5} color="#e8e8e8" />
            <AnimatePresence>
              {selectedNode && !isDemoMode && (
                <motion.div
                  key="settings"
                  initial={{opacity: 0, x: 30}}
                  animate={{opacity: 1, x: 0}}
                  exit={{opacity: 0, x: 30}}
                  transition={{duration: 0.1, ease: "easeInOut"}}
                  className="absolute right-2 top-2 bottom-2 z-[99999]"
                >
                  <Settings
                    selectedNode={selectedNode}
                    takeSnapshot={takeSnapshot}
                    assistantType={assistantType}
                  />
                </motion.div>
              )}
              {showPrompt && !isDemoMode && (
                <>
                  <motion.div
                    key="prompt-drawer"
                    initial={{opacity: 0, x: 0}}
                    animate={{opacity: 1, x: 0}}
                    exit={{opacity: 0, x: 0}}
                    transition={{duration: 0.1, ease: "easeInOut"}}
                    className="absolute z-10 w-full h-full backdrop-blur-sm bg-[#16161D91]"
                    onClick={() => setShowPrompt(false)}
                  ></motion.div>
                </>
              )}
            </AnimatePresence>
            <PageLeaveGuard />
            <RunTests />
            <SearchDrawer />
            <Sections />
            <Variables />
            <History />
          </ReactFlow>
        )}
      </section>
    </>
  );
};

const CanvasWithProvider: React.FC<CanvasProps> = ({className}) => {
  const isDemoMode = FlowBuilderSettings.demoMode;

  // Load Amplitude Engagement SDK
  useEffect(() => {
    if (process.env.AMPLITUDE_DISABLED === "true") return;
    const apiKey = process.env.AMPLITUDE_API_KEY;
    const surveyKey = process.env.AMPLITUDE_SURVEY_KEY;

    if (!apiKey || !surveyKey) {
      console.error(
        "Missing Amplitude API key or Survey key in environment variables",
      );
      return;
    }

    // Initialize Amplitude Analytics SDK first
    amplitude.init(apiKey, {
      serverZone: "EU",
      defaultTracking: {
        sessions: true,
        pageViews: true,
      },
    });

    // Load Amplitude Engagement SDK
    const script = document.createElement("script");
    script.src = `https://cdn.eu.amplitude.com/script/${apiKey}.engagement.js`;
    script.async = true;

    script.onload = async () => {
      try {
        console.log("Amplitude Engagement SDK loaded");

        // Boot the engagement SDK with Analytics integration
        await window.engagement.boot({
          user: {
            user_id: FlowBuilderSettings.modelId,
          },
          serverZone: "EU",
          integrations: [
            {
              track: event => {
                analytics.track(event.event_type, event.event_properties);
              },
            },
          ],
        });

        console.log(
          "Amplitude Engagement booted successfully with Analytics integration",
        );

        // Close any existing surveys and show the new one
        window.engagement.gs.closeAll();
        window.engagement.gs.show(surveyKey);

        console.log(`Survey ${surveyKey} displayed`);
      } catch (error) {
        console.error("Error initializing Amplitude Engagement:", error);
      }
    };

    script.onerror = () => {
      console.error("Failed to load Amplitude Engagement SDK");
    };

    document.head.appendChild(script);
  }, []);

  return (
    <ReactQueryProvider>
      <TooltipProvider>
        <ReactFlowProvider>
          <VariablesProvider>
            <SubflowsProvider>
              <SaveProvider>
                <DrawerProvider>
                  <CopyPasteProvider>
                    {!isDemoMode && <Topbar />}
                    <ActionListContextProvider actionType="custom">
                      <ActionDetailsContextProvider actionType="custom">
                        <Canvas className={className} />
                      </ActionDetailsContextProvider>
                    </ActionListContextProvider>
                  </CopyPasteProvider>
                </DrawerProvider>
              </SaveProvider>
            </SubflowsProvider>
          </VariablesProvider>
        </ReactFlowProvider>
      </TooltipProvider>
    </ReactQueryProvider>
  );
};

export default CanvasWithProvider;
